import { z } from 'zod';
import { feedLayout } from './topics.js';
import { uiLanguage } from './i18n.js';

// --- admin section (spec 2026-09-23): read-only statistics for ADMIN_EMAILS ---

export const AI_USAGE_KINDS = ['embedding', 'image', 'tags', 'junk_rules', 'translation'] as const;
export const aiUsageKind = z.enum(AI_USAGE_KINDS);
export type AiUsageKind = z.infer<typeof aiUsageKind>;

export const adminRange = z.enum(['7d', '30d', '90d']);
export type AdminRange = z.infer<typeof adminRange>;
export const adminRangeQuery = z.object({ range: adminRange.default('30d') });

export const adminAiKindStats = z.object({
  kind: aiUsageKind,
  calls: z.number().int(),
  failed: z.number().int(),
  costUsd: z.number(),
  avgDurationMs: z.number().int().nullable(),
  lastError: z.object({ message: z.string(), at: z.string() }).nullable(),
  // calls in the last 24h from now, independent of the selected range — drives the
  // "enabled but idle" red indicator (spec §6.1), which must not depend on range length
  calls24h: z.number().int(),
});
export type AdminAiKindStats = z.infer<typeof adminAiKindStats>;

export const adminOverview = z.object({
  users: z.object({
    total: z.number().int(),
    activeToday: z.number().int(),
    active7d: z.number().int(),
    premium: z.number().int(),
  }),
  // perDayAvg: reads of all users over the last 30 days / 30 (user_article_state is pruned with clusters)
  reads: z.object({ today: z.number().int(), perDayAvg: z.number() }),
  ai: z.array(adminAiKindStats),
  dedup: z.object({
    articles: z.number().int(),
    clusters: z.number().int(),
    multiSourceShare: z.number().min(0).max(1), // articles in clusters with ≥ 2 articles / articles, 0..1
    embeddingCoverage: z.number().min(0).max(1), // articles with an embedding / articles, 0..1
    sizeHistogram: z.array(z.object({ bucket: z.enum(['1', '2', '3-4', '5+']), count: z.number().int() })),
  }),
  tags: z.object({
    coverage: z.number().min(0).max(1), // tagged_at set / articles, 0..1
    emptyShare: z.number().min(0).max(1), // tagged but empty tags / tagged, 0..1 (= tagging failed)
    categories: z.array(z.object({ category: z.string(), count: z.number().int() })),
    topTags: z.array(z.object({ tag: z.string(), uses: z.number().int() })),
  }),
  images: z.object({ generated: z.number().int(), failed: z.number().int() }),
  translations: z.object({
    byLang: z.array(
      z.object({ lang: z.string(), cards: z.number().int(), fullOk: z.number().int(), fullFailed: z.number().int() }),
    ),
  }),
  breaking: z.object({ notifications: z.number().int(), clusters: z.number().int() }),
  worker: z.object({ lastFetchRunAt: z.string().nullable(), lastFetchOk: z.boolean().nullable() }),
  // from env: distinguishes "switched off" from "broken"
  aiEnabled: z.object({ embedding: z.boolean(), images: z.boolean(), tags: z.boolean(), translation: z.boolean() }),
});
export type AdminOverview = z.infer<typeof adminOverview>;

export const adminTimeseriesQuery = adminRangeQuery.extend({ kind: aiUsageKind.optional() });
export const adminTimeseries = z.object({
  days: z.array(
    z.object({ date: z.string(), calls: z.number().int(), failed: z.number().int(), costUsd: z.number() }),
  ),
});
export type AdminTimeseries = z.infer<typeof adminTimeseries>;

export const adminErrorsQuery = z.object({
  kind: aiUsageKind.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const adminErrors = z.object({
  items: z.array(
    z.object({
      id: z.uuid(),
      kind: aiUsageKind,
      model: z.string().nullable(),
      error: z.string().nullable(),
      articleId: z.uuid().nullable(),
      createdAt: z.string(),
    }),
  ),
});
export type AdminErrors = z.infer<typeof adminErrors>;

export const adminUsersSort = z.enum(['lastActive', 'reads', 'created']);
export type AdminUsersSort = z.infer<typeof adminUsersSort>;
export const adminUsersQuery = z.object({
  sort: adminUsersSort.default('lastActive'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AdminUsersQuery = z.infer<typeof adminUsersQuery>;

export const adminUserRow = z.object({
  id: z.uuid(),
  email: z.string(),
  createdAt: z.string(),
  // greatest of last login (sessions.created_at) and last read/save — null for a user who never did anything
  lastActiveAt: z.string().nullable(),
  premiumUntil: z.string().nullable(),
  feeds: z.number().int(),
  devices: z.number().int(),
  readsToday: z.number().int(),
  readsPerDay30: z.number(),
  saved: z.number().int(),
  translationsRequested30: z.number().int(),
});
export type AdminUserRow = z.infer<typeof adminUserRow>;
export const adminUsers = z.object({ items: z.array(adminUserRow), nextCursor: z.string().nullable() });
export type AdminUsers = z.infer<typeof adminUsers>;

export const adminUserDetail = z.object({
  user: adminUserRow,
  readsByDay: z.array(z.object({ date: z.string(), reads: z.number().int() })),
  categories: z.array(z.object({ category: z.string(), feeds: z.number().int() })),
  mutes: z.number().int(),
  settings: z.object({
    language: uiLanguage,
    translateContent: z.boolean(),
    notificationsEnabled: z.boolean(),
    feedLayout,
  }),
});
export type AdminUserDetail = z.infer<typeof adminUserDetail>;
